import React, { useLayoutEffect } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { async_softwareEmptyStateExperienceFeatureFlags_FeatureFlagEmptyStateExperienceEntryPoint$key } from '@atlassian/jira-relay/src/__generated__/async_softwareEmptyStateExperienceFeatureFlags_FeatureFlagEmptyStateExperienceEntryPoint.graphql';
import QUERY, {
	type uiFeatureFlagsEmptyStateExperienceQuery,
} from '@atlassian/jira-relay/src/__generated__/uiFeatureFlagsEmptyStateExperienceQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import type { OnInstallSuccess } from '../common/types.tsx';
import { useViewFeatureFlagsEmptyStateExperienceExperienceFail } from '../services/experience-tracker/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const FeatureFlagEmptyStateExperienceQueryLoader = lazy(
	() =>
		import(
			/* webpackChunkName: "async-software-empty-state-experience-feature-flags" */ './index'
		).then((mod) => mod.FeatureFlagsEmptyStateExperienceQueryLoader),
	{ ssr: false },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Loading = styled.div({
	display: 'grid',
	placeItems: 'center',
	paddingTop: token('space.1000'),
	paddingRight: '0px',
	paddingBottom: token('space.1000'),
	paddingLeft: '0px',
});

/**
 * Entry point for the empty state tutorial component. Use this if you require
 * both the query loading and component rendering to occur.
 */
export const FeatureFlagEmptyStateExperienceEntryPoint = ({
	isOpen,
	onClose,
	devOpsFeatureFlagProviders,
	onInstallSuccess,
}: {
	isOpen: boolean;
	onClose: () => void;
	devOpsFeatureFlagProviders: async_softwareEmptyStateExperienceFeatureFlags_FeatureFlagEmptyStateExperienceEntryPoint$key;
	onInstallSuccess: OnInstallSuccess;
}): React.JSX.Element | null => {
	const [queryReference, loadQuery] =
		useQueryLoader<uiFeatureFlagsEmptyStateExperienceQuery>(QUERY);
	const { data: projectContext } = useProjectContext();
	const cloudId = useCloudId();
	const onExperienceFail = useViewFeatureFlagsEmptyStateExperienceExperienceFail();

	const featureFlagProviders = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment async_softwareEmptyStateExperienceFeatureFlags_FeatureFlagEmptyStateExperienceEntryPoint on DevOpsFeatureFlagProvider
			@relay(plural: true) {
				...ui_softwareEmptyStateExperienceFeatureFlags_FeatureFlagsEmptyStateExperienceQueryLoader_devOpsFeatureFlagProviders
			}
		`,
		devOpsFeatureFlagProviders,
	);

	useLayoutEffect(() => {
		if (projectContext?.projectId != null && isOpen) {
			loadQuery(
				{
					projectAri: createAri({
						resourceId: projectContext.projectId.toString(),
						resourceOwner: 'jira',
						resourceType: 'project',
						cloudId,
					}),
				},
				{ fetchPolicy: 'store-and-network' },
			);
		}
	}, [isOpen, loadQuery, cloudId, projectContext]);

	return projectContext != null && isOpen ? (
		<>
			<JSErrorBoundary
				fallback="flag"
				id="featureFlagEmptyStateExperienceEntryPoint"
				packageName="jiraSoftwareEmptyStateExperienceFeatureFlags"
				teamName="jolt"
				onError={(location, error) => {
					onExperienceFail(location, error);
					onClose();
				}}
			>
				<Placeholder
					name="software-empty-state-experience-feature-flags"
					fallback={
						<Loading>
							<Spinner size="large" />
						</Loading>
					}
				>
					{queryReference != null ? (
						<FeatureFlagEmptyStateExperienceQueryLoader
							onInstallSuccess={onInstallSuccess}
							devOpsFeatureFlagProviders={featureFlagProviders}
							queryReference={queryReference}
						/>
					) : null}
				</Placeholder>
			</JSErrorBoundary>
		</>
	) : null;
};
